import { useMutation } from '@tanstack/react-query'
import { SubmitHandler, useFormContext } from 'react-hook-form'
import { useCallback } from 'react'
import { Link } from 'react-router'
import BackNavLink from 'core/components/navigation/BackNavLink'
import APIService from 'api/APIService'
import { AuthFormValue } from 'pages/auth/type'
import { ErrorMessage } from 'types/entity'
import { useUserProfile } from 'core/hooks/data/useDataHook'
import useNavigation from 'core/hooks/navigation/useNavigation'
import { ERROR_PASSWORD_NOT_JUST_WHITESPACE } from 'pages/account/profile/validate'
import Button from 'core/components/buttons/Button'
import SmartField from 'core/components/form/SmartField'
import Input from 'core/components/inputs/Input'
import FeatureIcon from 'core/components/icons/FeatureIcon'
import useAuth from 'core/hooks/auth/useAuth'
import { StartUrl } from 'constants/RouteUrls'
import useLogin from 'core/hooks/auth/useLogin'
import { getErrorMessage } from 'utils/AppHelper'

const LoginPage = () => {
  const { search } = useNavigation()
  const urlParams = new URLSearchParams(search)
  const gt = urlParams.get('gt') || null
  const { control, handleSubmit } = useFormContext<AuthFormValue>()
  const { setData } = useUserProfile()
  const { setUserId, setAuthLoading } = useAuth()
  const { handleEverlanceLogin } = useLogin()

  const {
    isPending: isLoading,
    isError,
    error,
    mutate
  } = useMutation({
    mutationKey: ['login'],
    mutationFn: APIService.users.login,
    onSuccess: (user) => {
      setAuthLoading(true)
      handleEverlanceLogin({
        user
      })

      setTimeout(() => {
        setData(user)
        setUserId(user.token_id.toString())
      }, 300)
    }
  })
  const onSubmit: SubmitHandler<AuthFormValue> = useCallback(
    ({ email, password }) => {
      mutate({
        email,
        password,
        gt,
        mixpanel_distinct_id: window?.mixpanel?.get_distinct_id?.()
      })
    },
    [gt, mutate]
  )

  return (
    <div
      data-title="login-screen"
      className="h-[88vh] flex justify-around items-center"
    >
      <div className="card bg-white mb-5 w-[500px]">
        <BackNavLink to={StartUrl} />
        <div>
          <form onSubmit={handleSubmit(onSubmit)}>
            <div className="flex justify-center">
              <FeatureIcon name="everlance" />
            </div>
            <div className="flex justify-center">
              <h3 className="pb-6" title="greeting">
                Welcome back!
              </h3>
            </div>
            <div className="mt-6 flex flex-col gap-4">
              <SmartField
                controllerProps={{
                  control,
                  name: 'email',
                  rules: {
                    required: 'Email is required',
                    validate: {
                      noBlank: (value) =>
                        value.trim().length > 0 ||
                        'Email cannot be just whitespace'
                    }
                  }
                }}
              >
                <Input
                  placeholder="email@email.com"
                  className="text-lg mb-2 w-full py-3.5"
                />
              </SmartField>

              <SmartField
                controllerProps={{
                  control,
                  name: 'password',
                  rules: {
                    required: 'Password is required',
                    validate: {
                      noBlank: (value) =>
                        value.trim().length > 0 ||
                        ERROR_PASSWORD_NOT_JUST_WHITESPACE
                    }
                  }
                }}
              >
                <Input
                  type="password"
                  placeholder="Enter your password"
                  autoFocus
                  className="text-lg mb-2 w-full py-3.5"
                />
              </SmartField>

              <Button
                type="submit"
                variant="primary"
                status={isLoading ? 'loading' : 'idle'}
                disabled={isLoading}
                title="submit-button"
                className="w-full"
              >
                Login
              </Button>
            </div>

            {isError && (
              <div>
                <p className="pt-2 text-danger">
                  {getErrorMessage(
                    error as ErrorMessage,
                    'An unknown error occurred'
                  )}
                </p>
              </div>
            )}
          </form>
          <div className="flex justify-center mt-12">
            <Link to="/password_resets/new" className="select-none">
              Forgot Password?
            </Link>
          </div>
        </div>
      </div>
    </div>
  )
}

export default LoginPage
